import { useLoader, useThree } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

import { useGameStore } from "../state";

export function useAudio(): [{ [key: string]: THREE.Audio | undefined }, THREE.AudioListener | undefined] {
  const [isAdded, setAdded] = useState(false);

  const { fxVolume, isMuted, mode, musicVolume } = useGameStore();

  const boostBuffer = useLoader(THREE.AudioLoader, "./audio/Boost.wav");
  const collideBuffer = useLoader(THREE.AudioLoader, "./audio/Collide.wav");
  const crowdBuffer = useLoader(THREE.AudioLoader, "./audio/Crowd.wav");
  const engineBuffer = useLoader(THREE.AudioLoader, "./audio/Engine.wav");
  const goBuffer = useLoader(THREE.AudioLoader, "./audio/Go.wav");
  const musicBuffer = useLoader(THREE.AudioLoader, "./audio/ChemicalBeats.mp3");
  const readyBuffer = useLoader(THREE.AudioLoader, "./audio/Ready.wav");

  const audioListener = useRef<THREE.AudioListener>();
  const boost = useRef<THREE.Audio>();
  const collide = useRef<THREE.Audio>();
  const crowd = useRef<THREE.Audio>();
  const engine = useRef<THREE.Audio>();
  const go = useRef<THREE.Audio>();
  const music = useRef<THREE.Audio>();
  const ready = useRef<THREE.Audio>();

  const { camera } = useThree();

  useEffect(() => {
    if (!isAdded) {
      audioListener.current = new THREE.AudioListener();

      boost.current = new THREE.Audio(audioListener.current).setBuffer(boostBuffer);
      collide.current = new THREE.Audio(audioListener.current).setBuffer(collideBuffer);
      crowd.current = new THREE.Audio(audioListener.current).setBuffer(crowdBuffer).setVolume(0).setLoop(true);
      engine.current = new THREE.Audio(audioListener.current).setBuffer(engineBuffer).setVolume(0).setLoop(true);
      go.current = new THREE.Audio(audioListener.current).setBuffer(goBuffer);
      music.current = new THREE.Audio(audioListener.current).setBuffer(musicBuffer).setLoop(true);
      ready.current = new THREE.Audio(audioListener.current).setBuffer(readyBuffer);

      camera.add(audioListener.current);

      setAdded(true);
    }
  }, [boostBuffer, camera, collideBuffer, crowdBuffer, engineBuffer, goBuffer, isAdded, musicBuffer, readyBuffer]);

  useEffect(() => {
    if (isMuted || mode === "camera") {
      boost.current?.setVolume(0);
      collide.current?.setVolume(0);
      go.current?.setVolume(0);
      ready.current?.setVolume(0);
      crowd.current?.pause();
      engine.current?.pause();
      music.current?.pause();
    } else {
      boost.current?.setVolume(0.75 * fxVolume);
      collide.current?.setVolume(0.75 * fxVolume);
      go.current?.setVolume(0.5 * fxVolume);
      ready.current?.setVolume(0.5 * fxVolume);
      music.current?.setVolume(0.5 * musicVolume);

      if (!crowd.current?.isPlaying) {
        crowd.current?.play();
      }

      if (!engine.current?.isPlaying) {
        engine.current?.play();
      }

      if (!music.current?.isPlaying) {
        music.current?.play();
      }
    }
  }, [fxVolume, isMuted, mode, musicVolume]);

  return [
    {
      boost: boost.current,
      collide: collide.current,
      crowd: crowd.current,
      engine: engine.current,
      go: go.current,
      music: music.current,
      ready: ready.current,
    },
    audioListener.current,
  ];
}
